import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { permissionGuard } from './core/guards/permission.guard';
import { PERMISSIONS } from './core/constants/permissions.constants';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./features/auth/reset-request/reset-request.component').then(
        (m) => m.ResetRequestComponent,
      ),
  },
  {
    path: 'reset-password/confirm',
    loadComponent: () =>
      import('./features/auth/reset-confirm/reset-confirm.component').then(
        (m) => m.ResetConfirmComponent,
      ),
  },
  {
    path: 'change-password',
    loadComponent: () =>
      import('./features/auth/change-password/change-password.component').then(
        (m) => m.ChangePasswordComponent,
      ),
  },
  {
    path: 'satisfaction/:ticketId',
    loadComponent: () =>
      import('./features/satisfaction/satisfaction-page.component').then(
        (m) => m.SatisfactionPageComponent,
      ),
  },
  {
    path: 'satisfaction-appel/:callId',
    loadComponent: () =>
      import('./features/satisfaction/public/satisfaction-appel/satisfaction-appel.component').then(
        (m) => m.SatisfactionAppelComponent,
      ),
  },
  {
    path: 'satisfaction-traitement/:ticketId',
    loadComponent: () =>
      import('./features/satisfaction/public/satisfaction-traitement/satisfaction-traitement.component').then(
        (m) => m.SatisfactionTraitementComponent,
      ),
  },
  {
    path: 'forbidden',
    loadComponent: () =>
      import('./features/auth/forbidden/forbidden.component').then((m) => m.ForbiddenComponent),
  },
  {
    path: '',
    loadComponent: () =>
      import('./layout/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'tickets',
        loadChildren: () =>
          import('./features/tickets/tickets.routes').then((m) => m.TICKETS_ROUTES),
      },
      {
        path: 'tickets-a-noter',
        canActivate: [permissionGuard(PERMISSIONS.TICKET_NOTER_SATISFACTION)],
        loadComponent: () =>
          import('./features/satisfaction/tickets-a-noter/tickets-a-noter.component').then(
            (m) => m.TicketsANoterComponent,
          ),
      },
      {
        path: 'satisfactions/notation-appel',
        canActivate: [permissionGuard(PERMISSIONS.TICKET_NOTER_SATISFACTION)],
        loadComponent: () =>
          import('./features/satisfaction/components/notation-appel/notation-appel.component').then(
            (m) => m.NotationAppelComponent,
          ),
      },
      {
        path: 'clients',
        loadChildren: () =>
          import('./features/clients/clients.routes').then((m) => m.CLIENTS_ROUTES),
      },
      {
        path: 'leads',
        canActivate: [permissionGuard(PERMISSIONS.LEAD_READ)],
        loadChildren: () => import('./features/leads/leads.routes').then((m) => m.LEADS_ROUTES),
      },
      {
        path: 'retours-clients',
        canActivate: [permissionGuard(PERMISSIONS.RETOUR_CLIENT_READ)],
        loadChildren: () =>
          import('./features/retours-clients/retours-clients.routes').then(
            (m) => m.RETOURS_CLIENTS_ROUTES,
          ),
      },
      {
        path: 'insatisfactions',
        canActivate: [permissionGuard(PERMISSIONS.INSATISFACTION_READ)],
        loadChildren: () =>
          import('./features/insatisfactions/insatisfactions.routes').then(
            (m) => m.INSATISFACTIONS_ROUTES,
          ),
      },
      {
        path: 'satisfactions',
        canActivate: [
          permissionGuard([
            PERMISSIONS.SATISFACTION_APPEL_READ,
            PERMISSIONS.SATISFACTION_TRAITEMENT_READ,
            PERMISSIONS.SATISFACTION_PRODUIT_READ,
            PERMISSIONS.SATISFACTION_RAPPORT_READ,
          ]),
        ],
        loadChildren: () =>
          import('./features/satisfaction/satisfaction.routes').then((m) => m.SATISFACTION_ROUTES),
      },
      {
        path: 'call-center',
        canActivate: [permissionGuard(PERMISSIONS.CALL_READ)],
        loadChildren: () =>
          import('./features/call-center/call-center.routes').then((m) => m.CALL_CENTER_ROUTES),
      },
      {
        path: 'vente-croisee',
        canActivate: [permissionGuard(PERMISSIONS.VENTE_CROISEE_READ)],
        loadComponent: () =>
          import('./features/vente-croisee/targeting.component').then((m) => m.TargetingComponent),
        data: {
          mode: 'cross',
          title: 'Vente crois\u00e9e',
          subtitle: 'Clients d\u2019autres filiales \u00e9ligibles aux offres de votre filiale.',
        },
      },
      {
        path: 'saturation',
        canActivate: [permissionGuard(PERMISSIONS.VENTE_CROISEE_READ)],
        loadComponent: () =>
          import('./features/vente-croisee/targeting.component').then((m) => m.TargetingComponent),
        data: {
          mode: 'saturation',
          title: 'Saturation',
          subtitle:
            'Clients de votre filiale qui ne disposent pas encore du produit s\u00e9lectionn\u00e9.',
        },
      },
      {
        path: 'messagerie',
        canActivate: [permissionGuard(PERMISSIONS.MESSAGERIE_READ)],
        loadComponent: () =>
          import('./features/messagerie/messagerie.component').then((m) => m.MessagerieComponent),
      },
      {
        path: 'telephonie/appels',
        loadComponent: () =>
          import('./features/telephonie/appels-entrants.component').then(
            (m) => m.AppelsEntrantsComponent,
          ),
      },
      {
        path: 'reporting',
        canActivate: [permissionGuard(PERMISSIONS.REPORTING_READ)],
        loadChildren: () =>
          import('./features/reporting/reporting.routes').then((m) => m.REPORTING_ROUTES),
      },
      {
        path: 'directeur',
        canActivate: [permissionGuard(PERMISSIONS.DIRECTEUR_VUE_GLOBALE_READ)],
        loadChildren: () =>
          import('./features/directeur/directeur.routes').then((m) => m.DIRECTEUR_ROUTES),
      },
      {
        path: 'admin',
        canActivate: [permissionGuard(PERMISSIONS.REFERENTIEL_GERER)],
        loadChildren: () =>
          import('./features/administration/administration.routes').then(
            (m) => m.ADMINISTRATION_ROUTES,
          ),
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
